import { translate as i18n } from "@/i18n";import { useLocale } from "@/i18n/useLocale";import React from "react";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, Lock, Loader, AlertTriangle } from "lucide-react";

const STATUS = {
  completado: { labelKey: "completado", cls: "text-emerald-700 bg-emerald-50 border-emerald-200", icon: CheckCircle2 },
  en_progreso: { labelKey: "en_progreso", cls: "text-sky-700 bg-sky-50 border-sky-200", icon: Loader },
  pendiente: { labelKey: "pendiente", cls: "text-amber-700 bg-amber-50 border-amber-200", icon: Circle },
  bloqueado: { labelKey: "bloqueado", cls: "text-slate-500 bg-slate-100 border-slate-200", icon: Lock },
  requiere_revision: { labelKey: "requiere_revision", cls: "text-rose-700 bg-rose-50 border-rose-200", icon: AlertTriangle },
  no_aplica: { labelKey: "no_aplica", cls: "text-slate-500 bg-slate-50 border-slate-200", icon: Circle },
  validado: { labelKey: "validado", cls: "text-emerald-700 bg-emerald-50 border-emerald-200", icon: CheckCircle2 },
  en_revision: { labelKey: "en_revision", cls: "text-sky-700 bg-sky-50 border-sky-200", icon: Loader },
  subido: { labelKey: "subido", cls: "text-slate-600 bg-slate-100 border-slate-200", icon: CheckCircle2 },
  rechazado: { labelKey: "rechazado", cls: "text-rose-700 bg-rose-50 border-rose-200", icon: AlertTriangle },
  incompleto: { labelKey: "incompleto", cls: "text-amber-700 bg-amber-50 border-amber-200", icon: AlertTriangle },
  uploading: { labelKey: "subiendo", cls: "text-sky-700 bg-sky-50 border-sky-200", icon: Loader },
  scanning: { labelKey: "escaneando_en_busca_de_virus", cls: "text-sky-700 bg-sky-50 border-sky-200", icon: Loader },
  ready: { labelKey: "disponible", cls: "text-emerald-700 bg-emerald-50 border-emerald-200", icon: CheckCircle2 },
  rejected: { labelKey: "archivo_rechazado", cls: "text-rose-700 bg-rose-50 border-rose-200", icon: AlertTriangle },
  failed: { labelKey: "error_de_subida", cls: "text-rose-700 bg-rose-50 border-rose-200", icon: AlertTriangle },
  pending: { labelKey: "pendiente", cls: "text-amber-700 bg-amber-50 border-amber-200", icon: Circle },
  provided: { labelKey: "adjuntado", cls: "text-emerald-700 bg-emerald-50 border-emerald-200", icon: CheckCircle2 },
  dismissed: { labelKey: "descartado_por_ti", cls: "text-slate-500 bg-slate-100 border-slate-200", icon: Circle }
};

export function StatusBadge({ status }) {
  useLocale();
  const s = STATUS[status] || STATUS.pendiente;
  const Icon = s.icon;
  return (
    <span data-testid={`status-${status}`} className={cn("inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border", s.cls)}>
      <Icon className="w-3.5 h-3.5" /> {i18n(`ui.components_common.${s.labelKey}`)}
    </span>);

}

export function ProgressBar({ value, className }) {
  return (
    <div className={cn("h-2.5 w-full rounded-full bg-slate-100 overflow-hidden", className)}>
      <div className="h-full rounded-full bg-[#2A9D8F] transition-all duration-700" style={{ width: `${value}%` }} />
    </div>);

}

export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div data-testid="empty-state" className="flex flex-col items-center justify-center text-center py-14 px-6">
      <div className="w-14 h-14 rounded-2xl bg-[#2A9D8F]/10 flex items-center justify-center mb-4">
        {Icon && <Icon className="w-7 h-7 text-[#2A9D8F]" />}
      </div>
      <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
      <p className="text-sm text-slate-500 mt-1 max-w-sm">{description}</p>
      {action && <div className="mt-5">{action}</div>}
    </div>);

}

export function WarningBanner({ children, tone = "amber" }) {
  const cls = tone === "amber" ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-slate-50 border-slate-200 text-slate-600";
  return (
    <div className={cn("flex items-start gap-2.5 text-sm rounded-xl border px-4 py-3", cls)}>
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <span>{children}</span>
    </div>);

}

export function SourceCard({ source }) {
  return (
    <a href={source.url} target="_blank" rel="noopener noreferrer" data-testid="source-card"
    className="block rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 hover:bg-white card-hover">
      <div className="text-xs font-semibold text-[#0F4C5C]">{source.authority}</div>
      <div className="text-sm text-slate-700">{source.title}</div>
      <div className="text-xs text-slate-400 mt-0.5">{i18n("ui.components_common.officialSourceDate", { date: source.date })}</div>
    </a>);

}

export function SectionTitle({ children, sub }) {
  return (
    <div className="mb-6">
      <h2 className="text-2xl font-semibold tracking-tight text-slate-900">{children}</h2>
      {sub && <p className="text-sm text-slate-500 mt-1">{sub}</p>}
    </div>);

}

export function Logo({ light, to = "/", className, compactOnMobile = false }) {
  return (
    <Link to={to} aria-label={i18n("ui.components_common.guia_emprendedor")} data-testid="brand-logo" className={cn("flex min-w-0 shrink-0 items-center gap-2 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2A9D8F] focus-visible:ring-offset-2", className)}>
      <img src="/brand-logo-72.webp" alt="" width="72" height="72" data-testid="brand-logo-image" className="h-9 w-9 shrink-0 rounded-xl object-cover" />
      <span className={cn("font-head font-semibold text-lg", compactOnMobile && "hidden sm:inline", light ? "text-white" : "text-[#0F4C5C]")}>{i18n("ui.components_common.guia_emprendedor")}</span>
    </Link>);

}
